/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getPveGuests, pveAction, type PveAction, type PveGuest } from "@/api/proxmox";
import { showToast } from "@/components/common/Toast";
import { formatBytes } from "@/utils/formatBytes";

const key = (g: PveGuest) => `${g.node}/${g.type}/${g.vmid}`;

/** VMs and containers of the Proxmox host, with power controls. Hidden until [proxmox] is configured. */
export const ProxmoxGuests: React.FC = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["pve-guests"], queryFn: getPveGuests, refetchInterval: 15_000, retry: false });
  // a destructive action needs a second click within a few seconds
  const [armed, setArmed] = useState<string | null>(null);
  useEffect(() => {
    if (!armed) return;
    const t = window.setTimeout(() => setArmed(null), 4000);
    return () => window.clearTimeout(t);
  }, [armed]);

  const run = useMutation({
    mutationFn: ({ g, a }: { g: PveGuest; a: PveAction }) => pveAction(g, a),
    onSuccess: () => {
      showToast("Command sent", "success");
      window.setTimeout(() => qc.invalidateQueries({ queryKey: ["pve-guests"] }), 2500);
    },
    onError: (e: Error) => showToast(e.message, "error"),
  });

  if (!data?.configured) return null;
  const guests = data.guests ?? [];
  const confirmOr = (g: PveGuest, a: PveAction) => {
    const id = `${key(g)}:${a}`;
    if (armed === id) {
      setArmed(null);
      run.mutate({ g, a });
    } else setArmed(id);
  };

  return (
    <section className="gh-w pve">
      <div className="gh-w-head">
        <span className="gh-label">Proxmox</span>
        <span className="gh-sub">{`${guests.filter((g) => g.status === "running").length}/${guests.length} running`}</span>
      </div>
      {data.error ? (
        <div className="gh-empty">{data.error}</div>
      ) : (
        <ul className="pve-list">
          {guests.map((g) => {
            const on = g.status === "running";
            return (
              <li key={key(g)}>
                <span className={`gh-status-dot ${on ? "sv-ok-bg" : "sv-idle-bg"}`} />
                <b>{g.name}</b>
                <em>{g.type === "lxc" ? "LXC" : "VM"}</em>
                <span className="pve-id">{`#${g.vmid}`}</span>
                <span className="pve-res">{on ? `${Math.round(g.cpu * 100)}% · ${formatBytes(g.mem)}` : "stopped"}</span>
                <span className="pve-act">
                  {on ? (
                    <>
                      <button type="button" onClick={() => confirmOr(g, "reboot")}>
                        {armed === `${key(g)}:reboot` ? "Sure?" : "Reboot"}
                      </button>
                      <button type="button" onClick={() => confirmOr(g, "shutdown")}>
                        {armed === `${key(g)}:shutdown` ? "Sure?" : "Shut down"}
                      </button>
                    </>
                  ) : (
                    <button type="button" className="pve-go" disabled={run.isPending} onClick={() => run.mutate({ g, a: "start" })}>
                      Start
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
